/**
 * Reports > Sales Tax (/reports/sales-tax).
 *
 * Taxable and non-taxable sales by store and tax authority for a range of business days
 * (the last complete calendar month unless a range is chosen), with tiles, a store
 * filter, a totals row and an Excel download laid out for filing. Replaces the QuickSight
 * dashboard "Taxable vs Nontaxable Sales for Sale Tax Filing", and its hard-coded store
 * list: store numbers and names come from the Cockpit store list.
 *
 * Thin page: the screen is the shared `SalesTax` component in `@ampersandbrands/ui`
 * (presentational); this file fetches through `useSalesTax`, keeps the range and the
 * store filter in the URL, and adds the page chrome.
 *
 * RBAC: head office only, `reports.salestax`. No store-manager view.
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available yet"
 * card on 404/503 while the api ships.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SalesTax as SalesTaxScreen, type FinanceDateRange } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { useReportPageState } from '@/lib/reportPageState';
import { isNotAvailable } from '@/lib/useStores';
import {
  buildFinanceQs,
  denverToday,
  downloadFinanceXlsx,
  parseRange,
  parseStores,
  useSalesTax,
  writeRange,
  writeStores,
} from '@/lib/useFinance';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';

/** The accessSlug that grants this report. Head office only. */
const SLUG = 'reports.salestax';

function SalesTaxInner() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, SLUG, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlRange = useMemo(() => parseRange(searchParams), [searchParams]);
  const stores = useMemo(() => parseStores(searchParams), [searchParams]);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const update = useCallback(
    (fn: (sp: URLSearchParams) => void) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          fn(sp);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const dataQ = useSalesTax(urlRange, stores, canView);
  const shownRange = urlRange ?? dataQ.range;

  const pageState = useMemo(
    () => [
      { label: 'Business days', value: shownRange ? `${shownRange.from} to ${shownRange.to}` : 'Last complete month' },
      { label: 'Stores', value: stores.length > 0 ? stores.join(', ') : 'All stores' },
    ],
    [shownRange, stores],
  );
  useReportPageState(pageState);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadFinanceXlsx('sales-tax/export', buildFinanceQs(urlRange, stores), 'sales-tax.xlsx');
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="Sales Tax" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="Sales Tax"
      subtitle="Taxable and non-taxable sales by store and tax authority, for filing. Without a range it shows the last complete calendar month. Refreshed from CounterPoint once a day."
      actions={<ToolPinToggle toolSlug="sales-tax" toolName="Sales Tax" />}
    />
  );

  if (isNotAvailable(dataQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Sales Tax" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <SalesTaxScreen
        rows={dataQ.rows}
        summary={dataQ.summary}
        storeOptions={dataQ.stores}
        range={shownRange}
        maxDate={denverToday()}
        asOf={dataQ.asOf}
        onRangeChange={(r: FinanceDateRange | null) => update((sp) => writeRange(sp, r))}
        filters={{ store_no: stores }}
        onFiltersChange={(f) => update((sp) => writeStores(sp, f.store_no))}
        loading={dataQ.isLoading}
        error={dataQ.error ? <ErrorCard message={dataQ.error.message} /> : undefined}
        onExport={() => void handleExport()}
        exporting={exporting}
      />
    </div>
  );
}

export function SalesTax() {
  return (
    <ErrorBoundary>
      <SalesTaxInner />
    </ErrorBoundary>
  );
}
